/*
 * Section admin : historique des rapports mensuels.
 * Les bilans PDF du mois écoulé sont archivés automatiquement à chaque envoi
 * (premier lundi du mois) et restent téléchargeables à tout moment, dans la
 * limite des 12 derniers mois. Un bouton permet aussi de générer ou de
 * rafraîchir l'archive du mois écoulé sans envoyer d'email.
 * Statistiques agrégées et anonymes uniquement, aucune donnée personnelle.
 */

import { useCallback, useEffect, useState } from "react";
import { CalendarDays, FileDown, FilePlus2, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  adminListMonthlyReports, adminDownloadMonthlyReportPdf, adminGenerateMonthlyReport,
} from "@/lib/api";

const sectionClass =
  "rounded-2xl bg-white border border-[rgba(35,82,119,0.14)] p-6 shadow-[var(--shadow-sm)] space-y-4";

/* Taille lisible en Ko. */
const formatSize = (bytes) => `${Math.max(1, Math.round((bytes || 0) / 1024))} Ko`;

/* Libellé avec majuscule initiale : "Mois de juillet 2026". */
const capitalize = (text) => (text ? text.charAt(0).toUpperCase() + text.slice(1) : "");

const ReportRow = ({ report, downloading, onDownload }) => (
  <li
    className="flex items-center justify-between gap-3 rounded-xl border border-[rgba(35,82,119,0.14)] bg-[rgba(226,246,255,0.3)] px-4 py-3"
    data-testid={`admin-monthly-report-${report.month_key}`}
  >
    <div>
      <p className="text-sm font-medium text-[#060606]">{capitalize(report.label)}</p>
      <p className="text-[11px] text-[#9E9E9E]">
        Archivé le {new Date(report.created_at).toLocaleDateString("fr-FR")} · {formatSize(report.size_bytes)}
      </p>
    </div>
    <Button
      type="button"
      variant="outline"
      size="sm"
      onClick={() => onDownload(report.month_key)}
      disabled={downloading === report.month_key}
      data-testid={`admin-monthly-report-download-${report.month_key}`}
      className="min-h-[36px] rounded-lg border-[rgba(35,82,119,0.3)] text-[#235277] hover:bg-white text-xs font-medium transition-colors duration-200"
    >
      <FileDown className="mr-1.5 w-3.5 h-3.5" aria-hidden="true" />
      {downloading === report.month_key ? "Préparation" : "Télécharger"}
    </Button>
  </li>
);

export const MonthlyReportsSection = ({ adminKey }) => {
  const [reports, setReports] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState("");
  const [generating, setGenerating] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await adminListMonthlyReports(adminKey);
      setReports(data.reports || []);
    } catch (e) {
      toast.error("Chargement impossible", {
        description: "L'historique des rapports n'a pas pu être récupéré.",
      });
    } finally {
      setLoading(false);
    }
  }, [adminKey]);

  useEffect(() => {
    load();
  }, [load]);

  const download = async (monthKey) => {
    setDownloading(monthKey);
    try {
      const blob = await adminDownloadMonthlyReportPdf(adminKey, monthKey);
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `RAPPORT_MENSUEL_CYCLIQ_${monthKey}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
      toast.success("Rapport téléchargé", { description: "L'archive du mois est prête." });
    } catch (e) {
      toast.error("Téléchargement impossible", { description: "Réessaie dans un instant." });
    } finally {
      setDownloading("");
    }
  };

  const generate = async () => {
    setGenerating(true);
    try {
      const result = await adminGenerateMonthlyReport(adminKey);
      toast.success("Rapport archivé", {
        description: `Le bilan du ${result.label} est disponible ci dessous.`,
      });
      await load();
    } catch (e) {
      toast.error("Génération impossible", { description: "Réessaie dans un instant." });
    } finally {
      setGenerating(false);
    }
  };

  return (
    <section className={sectionClass} data-testid="admin-monthly-reports-section">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="flex items-center gap-2 text-[#235277]">
            <CalendarDays className="w-4 h-4" aria-hidden="true" />
            <h2 className="font-heading text-base font-semibold text-[#060606]">
              Historique des rapports mensuels
            </h2>
          </div>
          <p className="mt-1 text-xs leading-relaxed text-[#9E9E9E]">
            Chaque bilan mensuel est archivé automatiquement lors de son envoi
            (premier lundi du mois) et reste téléchargeable ici, sur 12 mois.
            Compteurs agrégés et anonymes uniquement.
          </p>
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={load}
          disabled={loading}
          data-testid="admin-monthly-reports-refresh-button"
          className="min-h-[36px] rounded-lg border-[rgba(35,82,119,0.3)] text-[#235277] hover:bg-[#E2F6FF] text-xs font-medium transition-colors duration-200"
        >
          <RefreshCw className={`mr-1.5 w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} aria-hidden="true" />
          Actualiser
        </Button>
      </div>

      {loading && !reports ? (
        <div className="space-y-2">
          <Skeleton className="h-[58px] rounded-xl" />
          <Skeleton className="h-[58px] rounded-xl" />
        </div>
      ) : reports && reports.length > 0 ? (
        <ul className="space-y-2" data-testid="admin-monthly-reports-list">
          {reports.map((report) => (
            <ReportRow
              key={report.month_key}
              report={report}
              downloading={downloading}
              onDownload={download}
            />
          ))}
        </ul>
      ) : (
        <p className="text-xs text-[#9E9E9E]" data-testid="admin-monthly-reports-empty">
          Aucun rapport archivé pour le moment. Le premier arrivera automatiquement
          au prochain premier lundi du mois, ou génère le dès maintenant.
        </p>
      )}

      <div className="flex justify-end">
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={generate}
          disabled={generating}
          data-testid="admin-monthly-reports-generate-button"
          className="min-h-[36px] rounded-lg border-[rgba(35,82,119,0.3)] text-[#235277] hover:bg-[#E2F6FF] text-xs font-medium transition-colors duration-200"
        >
          <FilePlus2 className="mr-1.5 w-3.5 h-3.5" aria-hidden="true" />
          {generating ? "Génération en cours" : "Générer le rapport du mois écoulé"}
        </Button>
      </div>
    </section>
  );
};
